'use client';
import React, { useCallback, useMemo, memo } from 'react';
import { AdvancedMarker, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { ClusterContent } from './ClusterContent';
import { markerConstant } from './marker-constant';

type TreeClusterMarkerProps = {
  clusterId: number;
  onMarkerClick?: (marker: google.maps.marker.AdvancedMarkerElement, clusterId: number) => void;
  position: google.maps.LatLngLiteral;
  size: number;
  sizeAsText: string;
  title?: string;
};

const FeaturesClusterMarkerComponent = ({
  position,
  size,
  sizeAsText,
  onMarkerClick,
  clusterId,
  title,
}: TreeClusterMarkerProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef();

  const handleClick = useCallback(
    () => onMarkerClick && onMarkerClick(marker!, clusterId),
    [onMarkerClick, marker, clusterId],
  );

  // Memoize marker size calculation
  const markerSize = useMemo(() => {
    if (size < 10) return markerConstant[0].size;
    if (size < 25) return markerConstant[1].size;
    if (size < 50) return markerConstant[2].size;
    if (size < 100) return markerConstant[3].size;
    return markerConstant[4].size;
  }, [size]);

  // Memoize style object to prevent recreation
  const markerStyle = useMemo(
    () => ({
      width: markerSize,
      height: markerSize,
    }),
    [markerSize],
  );

  return (
    <AdvancedMarker
      title={title}
      ref={markerRef}
      position={position}
      zIndex={size}
      onClick={handleClick}
      style={markerStyle}
      className={'transition-transform duration-300 ease-in-out transform hover:scale-110'}
    >
      <ClusterContent count={size} sizeAsText={sizeAsText} />
    </AdvancedMarker>
  );
};

// Memoize to prevent re-rendering when props haven't changed
export const FeaturesClusterMarker = memo(FeaturesClusterMarkerComponent);
